import {inject, DOM, customElement, LogManager, bindable, computedFrom} from 'aurelia-framework';
import {noop, once, debounce} from 'lodash';
import bezier from 'bezier-easing';
import Tween from './tween.engine';
import {camelCasedStyleProp} from '../../../tools/bfdt';
import erd from 'element-resize-detector';

const easeOutSine = bezier(0.39, 0.575, 0.565, 1);
const logger = LogManager.getLogger('ScrollingArea');
logger.setLevel(LogManager.logLevel.info);

@inject(DOM.Element)
@customElement('scrolling-area')
export class ScrollingArea {

  private __scrollEl: HTMLElement;
  private areaHeight: number = 0;
  private scrollHeight: number = 0;

  @bindable
  currentTranslate: number = 0;

  private __eventTime: number = 0;
  private __prevTranslate: number = 0;
  /** Magic mouse, etc. */
  private __pixelScrollDevice: boolean = false;
  private __transformProp: string = camelCasedStyleProp('transform');
  private __downDirection: boolean = true;
  private isMouseOver: boolean = false;
  private erd: erd.Erd = erd({strategy: 'scroll'});
  private __onAttachOnce: Function;
  private __easing: Function;
  private __tween: Tween;

  @bindable easing: string = 'easeOutSine';
  @bindable duration: number = 350;
  @bindable wheelPropagate: boolean = true;
  @bindable minSl: number | null = 60;
  /** Max scrollbar slider length */
  @bindable maxSl: number | null = null;
  @bindable extended: boolean = true;
  @bindable reachEnd: Function = noop;
  @bindable disabled: boolean = false;
  @bindable resize: Function = noop;
  @bindable debounce: number = 100;

  @computedFrom('isMouseOver', 'disabled')
  get isVisible() {
    const visible = this.isMouseOver && !this.disabled;
    if (visible) this.update();
    return visible;
  }

  update() {
    this.__updateDimensions(true);
  }

  constructor(private __areaEl: Element) {
    this.__onAttachOnce = once(() => {
      this.__easing = this.__makeEasing();
      this.__scrollEl = this.__areaEl.querySelector(':first-child');

      this.currentTranslate = 0;

      this.__initTweenEngine();
    });
  }

  attached() {
    this.__onAttachOnce();
    this.__updateDimensions(true);
    this.erd.listenTo(
      [this.__scrollEl, this.__areaEl] as any,
      debounce(() => {
        this.resize();
        this.update();
      }, this.debounce)
    );
  }

  detached() {
    // this.__observer.detach();
    this.erd.uninstall([this.__scrollEl, this.__areaEl] as any);
  }

  currentTranslateChanged(value) {
    // this.__scrollEl.style[this.__transformProp] = `translate3d(0,${value}px,0)`;
    this.__scrollEl.style[this.__transformProp] = `translateY(${value}px)`;
  }

  /**
   * @param {number} delta
   * @param {Event} e
   */
  onWheel(delta, e) {
    let dimensions = this.__getDimensions();
    if (dimensions.scrollHeight <= dimensions.areaHeight) {
      if (!this.wheelPropagate) e.preventDefault();
      return;
    }

    if (e.timeStamp - this.__eventTime >= 300) {
      this.__pixelScrollDevice = Math.abs(delta) <= 10;
    }

    let downDirection = delta > 0;
    let isDirectionChanged = this.__downDirection !== downDirection;
    this.__downDirection = downDirection;

    let translate;
    if (this.__tween.isRunning()) {
      if (isDirectionChanged) {
        translate = this.currentTranslate;
      } else {
        translate = this.__prevTranslate;
      }
    } else {
      translate = dimensions.currentTranslate;
    }

    let value;
    let onEnd;
    if (downDirection) {
      // scroll down
      let th = dimensions.areaHeight + delta - translate;
      if (th >= dimensions.scrollHeight) {
        value = dimensions.areaHeight - dimensions.scrollHeight;
        onEnd = () => this.reachEnd();
      } else {
        value = translate - delta;
      }
    } else {
      // scroll up
      delta = translate - delta;
      if (delta > 0) {
        value = 0;
      } else {
        value = delta;
      }
    }

    let immediate = this.__pixelScrollDevice;

    e.preventDefault();
    this.__eventTime = e.timeStamp;

    if (translate === value) {
      // TODO: scroll bar upate
      return;
    }

    this.__prevTranslate = value;
    // console.log(`delta=${delta}px, translate=${translate}px, value=${value}px`);
    this.__runAnimation({value: value, immediate: immediate, complete: onEnd});
    //TODO: scroll bar update
  }

  /**
   * @param {number} value
   * @param {boolean} immediate
   * @param {Function} [complete]
   * @private
   */
  __runAnimation({value, immediate, complete = noop}) {
    this.__tween.stop();

    if (immediate) {
      this.currentTranslate = value;
      complete();
      return;
    }

    let startTranslate = this.currentTranslate;
    let delta = value - startTranslate;
    this.__tween.run({
      complete,
      duration: this.duration,
      step: (pc) => {
        this.currentTranslate = delta * this.__easing(pc) + startTranslate;
        //TODO: some move callback
      }
    });
  }

  private __getDimensions() {
    this.__updateAreaHeight();
    this.__updateScrollHeight();

    return {
      areaHeight: this.areaHeight,
      scrollHeight: this.scrollHeight,
      currentTranslate: this.currentTranslate
    };
  }

  private __updateAreaHeight(force?: boolean) {
    if (this.areaHeight && force !== true) {
      return;
    }

    let style = DOM.getComputedStyle(this.__areaEl);
    if (style) {
      this.areaHeight = parseFloat(style.height);
    }
  }

  private __updateScrollHeight(force?: boolean) {
    if (this.scrollHeight && force !== true) {
      return;
    }

    let style = DOM.getComputedStyle(this.__scrollEl);
    if (style) {
      this.scrollHeight = parseFloat(style.height) + parseFloat(style.borderTopWidth)
        + parseFloat(style.borderBottomWidth) + parseFloat(style.paddingTop)
        + parseFloat(style.paddingBottom) + parseFloat(style.marginTop)
        + parseFloat(style.marginBottom);
    }
  }

  /**
   * @param {boolean} [force=false]
   * @private
   */
  __updateDimensions(force) {
    this.__updateAreaHeight(force);
    this.__updateScrollHeight(force);

    let delta = (this.areaHeight - this.currentTranslate) - this.scrollHeight;
    if (delta > 0) {
      let value = this.currentTranslate + delta;
      this.currentTranslate = value > 0 ? 0 : value;
    }
  }

  /**
   * @private
   */
  __initTweenEngine() {
    if (this.__tween) return;
    this.__tween = new Tween(this.__scrollEl);
  }

  private __makeEasing(): Function {
    switch (this.easing) {
    case 'linear':
      return function(pct) {
        return pct;
      };
    case 'easeOutSine':
    default:
      return easeOutSine;
    }
  }

  private __handleMutations(mutations: MutationRecord[]) {
    // console.time('MUTATIONS');
    let area = false;
    let other = area;
    for (let i = mutations.length - 1; i >= 0; i--) {
      let mutation = mutations[i];
      // for (let mutation of mutations) {
      if (mutation.target === this.__areaEl && !area) {
        area = true;
        this.__updateAreaHeight(true);
      } else if (!other) {
        other = true;
        this.__updateScrollHeight(true);
      }

      if (area && other) {
        break;
      }
    }
    // console.timeEnd('MUTATIONS');
  }

}

// class Observer {
//
//   /**
//    * @param {Element} element
//    * @param {Function} callback
//    */
//   constructor(element, callback) {
//     /**
//      * @type {Element}
//      * @private
//      */
//     this.__targetEl = element;
//     /**
//      * @type {MutationObserver}
//      * @private
//      */
//     this.__observer = DOM.createMutationObserver(callback);
//   }
//
//   /** @public */
//   attach() {
//     this.__observer.observe(this.__targetEl, {
//       childList: true,
//       subtree: true,
//       attributes: true,
//       attributeFilter: ['class']
//     });
//   }
//
//   /** @public */
//   detach() {
//     this.__observer.disconnect();
//   }
//
// }
